import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal, AnimatedCounter, ServiceCard, TestimonialCard, CTASection } from '../components/UI';
import IMAGES from '../images';

const Home = () => {
  const services = [
    { icon: '🎯', title: 'Google Ads Management', description: 'Stop wasting money on ads that don\'t convert. Our certified specialists create laser-focused campaigns that target your ideal customers.', features: ['Strategic Keyword Research', 'Conversion Tracking', 'A/B Testing', 'Monthly Reports'], link: '/services/google-ads' },
    { icon: '📍', title: 'Local SEO', description: 'Dominate local search results. We optimize your Google Business Profile and build citations to drive calls and foot traffic.', features: ['GBP Optimization', 'Citation Building', 'Review Management', 'Local Rankings'], link: '/services/local-seo' },
    { icon: '🚀', title: 'Technical SEO', description: 'Your website\'s technical foundation determines rankings. We uncover and fix hidden issues killing your visibility.', features: ['Technical Audits', 'Core Web Vitals', 'Site Speed', 'Schema Markup'], link: '/services/technical-seo' },
    { icon: '📊', title: 'Analytics & Reporting', description: 'Data without insight is just numbers. We transform your marketing data into actionable intelligence that drives growth.', features: ['Custom Dashboards', 'Call Tracking', 'ROI Tracking', 'Strategy Sessions'], link: '/services/analytics' },
  ];

  const caseStudies = [
    { name: 'A*** Roofing Co.', industry: 'Roofing', image: IMAGES.caseStudy1, highlight: '+312% Traffic', stat: '1.17K clicks' },
    { name: 'B*** Home Services', industry: 'HVAC', image: IMAGES.caseStudy2, highlight: '12.7% CTR', stat: '26.7K clicks' },
    { name: 'C*** Contractors', industry: 'General Contractor', image: IMAGES.caseStudy3, highlight: 'Position 3.6', stat: '19K clicks' },
  ];

  const testimonials = [
    { name: 'Mike Johnson', company: 'Johnson Roofing', text: 'Crescit completely transformed our business. We went from 5 leads a month to over 40. Best investment we\'ve ever made.' },
    { name: 'Sarah Chen', company: 'Chen Dental Group', text: 'Our new patient bookings increased by 200% in just 4 months. The team actually knows what they\'re doing.' },
    { name: 'Robert Martinez', company: 'Martinez HVAC', text: 'Finally, an agency that delivers real results. Our cost-per-lead dropped by 50% and lead quality improved dramatically.' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: Math.random() * 300 + 50,
              height: Math.random() * 300 + 50,
              background: `radial-gradient(circle, rgba(255,107,53,${Math.random() * 0.1}) 0%, transparent 70%)`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }} />
          ))}
        </div>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '120px 24px 80px', position: 'relative', zIndex: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 50, alignItems: 'center' }}>
            <div>
              <div style={{ marginBottom: 20 }}>
                <span style={{ background: 'rgba(255,107,53,0.2)', color: '#ff6b35', padding: '8px 18px', borderRadius: 50, fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,107,53,0.3)' }}>
                  🚀 Trusted by 50+ Growing Businesses
                </span>
              </div>
              <h1 style={{ fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: 20 }}>
                Stop Losing Customers to Your Competition
              </h1>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, marginBottom: 16 }}>
                Your competitors are stealing your customers right now. Every day you're not on the first page of Google, you're handing them money.
              </p>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: 28 }}>
                We help local businesses dominate Google search through strategic Google Ads and proven SEO strategies. Our clients see an average <strong style={{ color: '#ff6b35' }}>312% increase in organic traffic</strong> and <strong style={{ color: '#ff6b35' }}>40% reduction in cost-per-lead</strong>.
              </p>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 32 }}>
                <Link to="/contact" className="btn-primary" style={{ padding: '16px 36px', borderRadius: 50, fontSize: 16 }}>
                  Get Your Free Strategy Call →
                </Link>
                <Link to="/portfolio" style={{ padding: '16px 36px', borderRadius: 50, background: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600, border: '2px solid rgba(255,255,255,0.3)', fontSize: 16 }}>
                  See Our Results
                </Link>
              </div>
              <div style={{ display: 'flex', gap: 32 }}>
                {[
                  { value: <AnimatedCounter end={500} suffix="K+" prefix="$" />, label: 'Ad Spend Managed' },
                  { value: <AnimatedCounter end={50} suffix="+" />, label: 'Happy Clients' },
                  { value: <AnimatedCounter end={312} suffix="%" />, label: 'Avg Traffic Growth' },
                ].map((stat, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#ff6b35' }}>{stat.value}</div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ color: 'white', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>🔥 What You're Missing Right Now</h3>
                <ul style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 1.9, listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>❌ Customers searching for your services... finding competitors</li>
                  <li>❌ Money wasted on ads that don't convert</li>
                  <li>❌ A website that doesn't rank for important keywords</li>
                  <li>❌ Leads going to businesses with better online presence</li>
                </ul>
              </div>
              <div style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.2), rgba(255,107,53,0.05))', borderRadius: 16, padding: 24, border: '1px solid rgba(255,107,53,0.3)' }}>
                <h3 style={{ color: 'white', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>✅ What Our Clients Get</h3>
                <ul style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 1.9, listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>✓ First page rankings for high-intent keywords</li>
                  <li>✓ Google Ads that generate leads at 40% lower cost</li>
                  <li>✓ A Google Business Profile that dominates the map</li>
                  <li>✓ Transparent reporting showing exactly what's working</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{ background: 'white', padding: '32px 24px', borderBottom: '1px solid #eee' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Industries We Serve</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 28, color: '#444', fontSize: 15, fontWeight: 500, flexWrap: 'wrap' }}>
            <span>🏠 Roofing</span><span>❄️ HVAC</span><span>🔧 Plumbing</span><span>🦷 Dental</span><span>⚖️ Legal</span><span>🏢 Property Mgmt</span>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Services</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>How We Grow Your Business</h2>
              <p style={{ color: '#666', maxWidth: 650, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                We don't offer a dozen different services. We focus on what works: Google Ads and SEO. And we're really, really good at them.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {services.map((service, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <ServiceCard {...service} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Case Studies</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>Real Results for Real Businesses</h2>
              <p style={{ color: '#666', maxWidth: 650, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                These are actual Google Search Console screenshots from real client campaigns. The results speak for themselves.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {caseStudies.map((study, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="card" style={{ background: '#f8f9fa', borderRadius: 16, overflow: 'hidden', transition: 'all 0.3s ease' }}>
                  <div style={{ height: 200, overflow: 'hidden' }}>
                    <img src={study.image} alt={study.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ padding: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{study.name}</h3>
                        <p style={{ color: '#888', fontSize: 13 }}>{study.industry}</p>
                      </div>
                      <div style={{ background: 'linear-gradient(135deg, #ff6b35, #f54e00)', padding: '8px 16px', borderRadius: 20 }}>
                        <span style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{study.highlight}</span>
                      </div>
                    </div>
                    <p style={{ color: '#666', fontSize: 14 }}>{study.stat} in organic search</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/portfolio" className="btn-secondary" style={{ padding: '14px 32px', borderRadius: 50, fontSize: 15, display: 'inline-block' }}>
              View All Case Studies →
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Testimonials</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10 }}>What Our Clients Say</h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {testimonials.map((t, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <TestimonialCard {...t} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process Preview */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Process</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10 }}>How We Get You Results</h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {[
              { num: '01', title: 'Discovery', desc: 'Comprehensive audit of your digital presence and competitors.' },
              { num: '02', title: 'Strategy', desc: 'Custom roadmap built specifically for your business goals.' },
              { num: '03', title: 'Execute', desc: 'Implementation with precision and regular communication.' },
              { num: '04', title: 'Optimize', desc: 'Continuous improvement based on real performance data.' },
            ].map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b35, #f54e00)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 22, fontWeight: 800, color: 'white' }}>
                    {step.num}
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>{step.title}</h3>
                  <p style={{ color: '#666', lineHeight: 1.7, fontSize: 15 }}>{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Link to="/process" className="btn-secondary" style={{ padding: '14px 32px', borderRadius: 50, fontSize: 15, display: 'inline-block' }}>
              Learn More About Our Process →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default Home;
